import React from 'react';
import { Field, FieldLabel, FieldError } from '@/components/ui/field';
import { getIn, useFormikContext } from 'formik';
import { cn } from '@/utils/utils';
import { ConnectedInput } from './ConnectedInput';

export type FormRowProps<
  P extends {
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
  },
> = {
  name: string;
  errorFields?: string[];
  hidden?: boolean;
  label?: React.ReactNode;
  Icon?: React.ElementType;
  inputComponent?: React.ComponentType<P>;
  size?: 'sm' | 'md' | 'lg';
  inputProps?: Omit<P, 'name' | 'id' | 'disabled' | 'className'> &
    Partial<Pick<P, 'name' | 'id' | 'disabled' | 'className'>>;
  disabled?: boolean;
};

export const FormRow = <
  P extends {
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
  },
>({
  name,
  hidden,
  label,
  Icon,
  disabled,
  size = 'md',
  inputProps,
  inputComponent: InputComponent = ConnectedInput as React.ComponentType<P>,
  errorFields,
}: FormRowProps<P>) => {
  const { touched, errors } = useFormikContext<any>();
  const allFields = [name, ...(errorFields ?? [])];
  const invalid = allFields.some(
    (field) => getIn(touched, field) && getIn(errors, field)
  );
  const firstError = allFields
    .map((field) =>
      getIn(touched, field) && getIn(errors, field)
        ? getIn(errors, field)
        : null
    )
    .filter(Boolean)[0];

  return hidden ? null : (
    <Field data-invalid={invalid || undefined}>
      <FieldLabel htmlFor={name} className={cn(size === 'lg' && 'text-lg')}>
        {Icon && (
          <Icon
            className={cn(
              'size-5 stroke-foreground',
              size === 'lg' && 'size-6'
            )}
          />
        )}
        {label}
      </FieldLabel>
      <InputComponent
        id={name}
        name={name}
        {...(inputProps as P)}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          size === 'lg' &&
            `h-12! text-lg! [&_input]:text-lg! [&_[data-slot=input-group-addon]_*]:text-lg! [&_svg]:size-5!
            `,
          inputProps?.className
        )}
      />
      <FieldError>
        {typeof firstError === 'string' ? firstError : null}
      </FieldError>
    </Field>
  );
};
